import { useState } from "react";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LegalAI = ({ user, onLogout }) => {
  const [query, setQuery] = useState("");
  const [context, setContext] = useState("");
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    const userQuery = query;
    
    try {
      const res = await axios.post(`${API}/ai/search`, {
        query: query,
        context: context || null
      });
      
      setHistory([...history, { query: userQuery, response: res.data.response }]);
      setResponse(res.data.response);
      setQuery("");
      setContext("");
      toast.success("تم الحصول على الإجابة");
    } catch (error) {
      toast.error("خطأ في البحث: " + (error.response?.data?.detail || "حاول مرة أخرى"));
    } finally {
      setLoading(false);
    }
  };

  const sampleQuestions = [
    "ما هي عقوبة السرقة في القانون العراقي؟",
    "ما هي شروط صحة عقد البيع في القانون المدني؟",
    "ما هي حقوق العامل في قانون العمل العراقي؟",
    "كيف يتم رفع دعوى مدنية في المحاكم العراقية؟"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-8" data-testid="legal-ai-page">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">البحث القانوني الذكي</h1>
          <p className="text-lg text-slate-600">اسأل أي سؤال قانوني واحصل على إجابة فورية من الذكاء الاصطناعي</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Search Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-purple-600" />
                  اطرح سؤالك القانوني
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Textarea
                      placeholder="مثال: ما هي عقوبة السرقة في القانون العراقي؟"
                      rows={4}
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      disabled={loading}
                      data-testid="ai-query-input"
                    />
                  </div>
                  <div>
                    <Textarea
                      placeholder="سياق إضافي (اختياري)"
                      rows={2}
                      value={context}
                      onChange={(e) => setContext(e.target.value)}
                      disabled={loading}
                      data-testid="ai-context-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full gap-2"
                    disabled={loading || !query.trim()}
                    data-testid="ai-search-button"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        جاري البحث...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        بحث
                      </>
                    )}
                  </Button>
                </form>

                {/* Current Response */}
                {response && (
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg" data-testid="ai-response">
                    <h3 className="font-bold text-blue-900 mb-2">الإجابة:</h3>
                    <div className="text-slate-700 whitespace-pre-wrap">{response}</div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* History */}
            {history.length > 0 && (
              <div className="mt-6 space-y-4">
                <h2 className="text-2xl font-bold text-slate-900">سجل البحث</h2>
                {history.map((item, index) => (
                  <Card key={index} data-testid={`history-item-${index}`}>
                    <CardContent className="pt-6">
                      <div className="mb-3">
                        <p className="font-semibold text-slate-900 mb-1">السؤال:</p>
                        <p className="text-slate-700">{item.query}</p>
                      </div>
                      <div>
                        <p className="font-semibold text-blue-900 mb-1">الإجابة:</p>
                        <p className="text-slate-600 whitespace-pre-wrap">{item.response}</p>
                      </div>
                    </CardContent>
                  </Card>
                )).reverse()}
              </div>
            )}
          </div>

          {/* Sidebar - Sample Questions */}
          <div>
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>أسئلة شائعة</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {sampleQuestions.map((q, index) => (
                    <button
                      key={index}
                      onClick={() => setQuery(q)}
                      className="w-full text-right p-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors text-sm text-slate-700"
                      data-testid={`sample-question-${index}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalAI;
